/**
 * Dashboard grid configuration.
 *
 * One thing to know before changing any of this: react-grid-layout matches
 * breakpoints against the **grid container's** width, not the window's. The
 * filters sidebar is 230px plus a gutter, so the container runs roughly 300px
 * narrower than the viewport whenever it is open — which is why these
 * thresholds sit well below the CSS breakpoints you would expect.
 */
export const GRID_BREAKPOINTS = { lg: 1100, md: 860, sm: 620, xs: 400, xxs: 0 };

/**
 * 24 columns at every breakpoint, deliberately.
 *
 * Layouts are authored in the builder and saved in 24-column coordinates. A
 * narrower grid puts those x/w values out of range, and RGL clamps x to
 * `cols - w` — so every widget past the first row lands in the same slot and
 * leaves a gap beside it. (That is exactly what an 8-column `xs` used to do:
 * a w=6 widget became 6/8 = 75% wide and nothing could sit next to it.)
 *
 * Responsiveness is done by rewriting the layout per breakpoint — see
 * buildResponsiveLayouts in helpers/Dashboard.ts — not by shrinking the grid.
 */
export const GRID_COLS = { lg: 24, md: 24, sm: 24, xs: 24, xxs: 24 };

export const GRID_TOTAL_COLS = 24;

/**
 * The narrowest a widget may be at each breakpoint, in 24ths. This is what
 * actually reflows the dashboard: a quarter-width KPI stays a quarter on a
 * desktop, becomes a half on a tablet, and goes full width on a phone rather
 * than shrinking to an unreadable sliver.
 *
 * 1 means "leave the authored layout alone".
 */
export const GRID_MIN_WIDTHS = { lg: 1, md: 1, sm: 12, xs: 24, xxs: 24 };

/** Gutters tighten as the screen shrinks so small cards keep their content. */
export const GRID_MARGINS: Record<string, [number, number]> = {
  lg: [16, 16],
  md: [16, 16],
  sm: [12, 12],
  xs: [8, 8],
  xxs: [8, 8],
};

/** Fallback for callers that don't vary margin by breakpoint. */
export const GRID_MARGIN: [number, number] = [16, 16];
export const GRID_CONTAINER_PADDING: [number, number] = [0, 0];

/** Row unit. Shorter rows on phones keep tall widgets from filling the screen. */
export const GRID_ROW_HEIGHTS: Record<string, number> = {
  lg: 72,
  md: 72,
  sm: 72,
  xs: 64,
  xxs: 64,
};

export const COLOR_LIST = [
  "#7cb5ec","#434348","#90ed7d","#f7a35c","#8085e9","#f15c80","#e4d354","#2b908f","#f45b5b","#91e8e1"
]
